import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, ChevronDown, ChevronUp, X } from "lucide-react";

/**
 * Cookie Consent — slim corner pill (Linear / Vercel style)
 *
 * UX:
 *  - First visit: pill appears bottom-right after a small delay
 *  - User can: Accept all  |  Reject non-essential  |  Customise
 *  - Customise expands the pill into a small panel with 3 toggles
 *  - Choice is persisted in localStorage with a VERSION tag — bumping the
 *    version below will re-prompt every visitor (use for policy updates)
 *  - The pill can be reopened any time by dispatching the global event:
 *      window.dispatchEvent(new CustomEvent('connexis:open-cookie-prefs'))
 *    — wired into the Footer "Cookie preferences" link.
 *
 *  Categories:
 *    necessary  → always on, cannot be toggled
 *    analytics  → off by default until accepted
 *    marketing  → off by default until accepted
 *
 *  When analytics is rejected, we set window.__connexisAnalyticsBlocked = true
 *  so any later-loading script (PostHog, etc.) can self-disable.
 */

const STORAGE_KEY = "connexis_cookie_consent";
const VERSION = "1.0";

const DEFAULT_PREFS = {
  necessary: true,
  analytics: false,
  marketing: false,
  version: VERSION,
  decidedAt: null,
};

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== VERSION) return null;  // re-prompt on version bump
    return parsed;
  } catch (_) {
    return null;
  }
}

function savePrefs(prefs) {
  const payload = { ...prefs, version: VERSION, decidedAt: new Date().toISOString() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (_) { /* ignore */ }

  // Surface analytics gate so any other script can read it
  window.__connexisAnalyticsBlocked = !payload.analytics;
  window.__connexisMarketingBlocked = !payload.marketing;
  // Custom event so listeners (e.g. analytics initialiser) can react
  window.dispatchEvent(new CustomEvent("connexis:cookie-prefs-updated", { detail: payload }));
  return payload;
}

export default function CookieConsent() {
  const [open, setOpen]           = useState(false);
  const [customise, setCustomise] = useState(false);
  const [prefs, setPrefs]         = useState(DEFAULT_PREFS);
  const [hidden, setHidden]       = useState(false);

  // iter99: never render the cookie banner inside an iframe OR on any /embed/*
  // route. Embed widgets are anonymous, zero-PII, and use no cookies — the
  // banner is irrelevant there AND it visually duplicates the parent page's
  // banner when the embed is rendered from the same React app (as on /docs).
  const isEmbedContext = (() => {
    if (typeof window === "undefined") return false;
    try {
      if (window.self !== window.top) return true;       // rendered in any iframe
    } catch (_) {
      return true;                                       // cross-origin iframe → access blocked → still iframe
    }
    return window.location.pathname.startsWith("/embed/");
  })();

  // On mount — read existing prefs; show pill only if none stored
  useEffect(() => {
    if (isEmbedContext) return;                          // iter99: don't poke localStorage in embed contexts
    const existing = loadPrefs();
    if (existing) {
      setPrefs(existing);
      // Set blockers based on stored choice
      window.__connexisAnalyticsBlocked = !existing.analytics;
      window.__connexisMarketingBlocked = !existing.marketing;
    } else {
      // First-time visitor — show pill after small delay so it doesn't flash
      const t = setTimeout(() => setOpen(true), 600);
      // Default: block until user decides
      window.__connexisAnalyticsBlocked = true;
      window.__connexisMarketingBlocked = true;
      return () => clearTimeout(t);
    }
  }, [isEmbedContext]);

  // Allow Footer or any UI to reopen prefs
  useEffect(() => {
    const handler = () => {
      const existing = loadPrefs() || DEFAULT_PREFS;
      setPrefs(existing);
      setCustomise(true);
      setOpen(true);
      setHidden(false);
    };
    window.addEventListener("connexis:open-cookie-prefs", handler);
    return () => window.removeEventListener("connexis:open-cookie-prefs", handler);
  }, []);

  const acceptAll = () => {
    const next = savePrefs({ necessary: true, analytics: true, marketing: true });
    setPrefs(next);
    setOpen(false);
  };

  const rejectAll = () => {
    const next = savePrefs({ necessary: true, analytics: false, marketing: false });
    setPrefs(next);
    setOpen(false);
  };

  const saveCustom = () => {
    const next = savePrefs({ necessary: true, analytics: !!prefs.analytics, marketing: !!prefs.marketing });
    setPrefs(next);
    setOpen(false);
  };

  const toggle = (key) => {
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  // iter99: bail completely in any embed/iframe context
  if (isEmbedContext) return null;

  // iter176: never inside a signed-in workspace. A partner has accepted our terms in their
  // agreement; a consent pill sitting over their own case list just reads as clutter.
  if (/^\/(introducer|lender|refer|hub)(\/|$)/.test(window.location.pathname)) return null;

  if (!open || hidden) return null;

  return (
    <div
      className="fixed bottom-5 left-5 z-[80] max-w-sm w-[calc(100%-2.5rem)] sm:w-[360px] rounded-2xl bg-white shadow-[0_20px_60px_rgba(15,23,42,0.20)] border border-slate-200 overflow-hidden animate-fade-up"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
      data-testid="cookie-consent"
    >
      {/* Header — slim pill style */}
      <div className="px-5 pt-5 pb-3 flex items-start gap-3">
        <span className="inline-grid place-items-center size-9 rounded-lg bg-[#0055FF]/[0.08] text-[#0055FF] ring-1 ring-[#0055FF]/15 flex-shrink-0">
          <Cookie className="size-4.5" strokeWidth={1.9} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[14.5px] font-semibold text-slate-900 tracking-tight">Cookies on connex.is</div>
          <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">
            We use a few necessary cookies to keep the platform running. With your permission, we'd also love to use
            analytics to make things better. Read our{" "}
            <Link to="/legal/cookies" className="text-[#0055FF] hover:underline">cookie policy</Link>.
          </p>
        </div>
        <button
          onClick={() => { setHidden(true); /* not saved — they get re-asked next visit */ }}
          className="text-slate-400 hover:text-slate-700 transition-colors -mt-1 -mr-1 p-1"
          aria-label="Close"
          data-testid="cookie-close"
        >
          <X className="size-3.5" strokeWidth={2.2} />
        </button>
      </div>

      {/* Customise panel */}
      {customise && (
        <div className="px-5 pb-2 border-t border-slate-100 pt-3 space-y-2.5">
          <CookieToggle
            label="Strictly necessary"
            description="Authentication, security, session. Always on."
            checked
            disabled
            testid="cookie-toggle-necessary"
          />
          <CookieToggle
            label="Analytics"
            description="Aggregate, anonymised usage so we can improve."
            checked={!!prefs.analytics}
            onChange={() => toggle("analytics")}
            testid="cookie-toggle-analytics"
          />
          <CookieToggle
            label="Marketing"
            description="Personalisation in marketing communications. Currently inactive."
            checked={!!prefs.marketing}
            onChange={() => toggle("marketing")}
            testid="cookie-toggle-marketing"
          />
        </div>
      )}

      {/* Actions */}
      <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/40">
        {!customise && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={acceptAll}
              className="flex-1 min-w-[120px] rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white text-[12.5px] font-medium h-9 px-4 transition-colors shadow-sm shadow-[#0055FF]/20"
              data-testid="cookie-accept-all"
            >
              Accept all
            </button>
            <button
              onClick={rejectAll}
              className="rounded-full border border-slate-300 hover:border-slate-400 text-slate-700 text-[12.5px] font-medium h-9 px-4 transition-colors"
              data-testid="cookie-reject-all"
            >
              Reject
            </button>
            <button
              onClick={() => setCustomise(true)}
              className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-slate-900 font-medium px-2 h-9 transition-colors"
              data-testid="cookie-customise"
            >
              Customise <ChevronDown className="size-3" />
            </button>
          </div>
        )}

        {customise && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={saveCustom}
              className="flex-1 min-w-[140px] rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white text-[12.5px] font-medium h-9 px-4 transition-colors shadow-sm shadow-[#0055FF]/20"
              data-testid="cookie-save-custom"
            >
              Save preferences
            </button>
            <button
              onClick={() => setCustomise(false)}
              className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-slate-900 font-medium px-2 h-9 transition-colors"
              data-testid="cookie-collapse"
            >
              <ChevronUp className="size-3" /> Hide
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CookieToggle({ label, description, checked, onChange, disabled, testid }) {
  return (
    <label
      className={`flex items-start gap-3 py-2 ${disabled ? "opacity-60 cursor-default" : "cursor-pointer"}`}
      data-testid={testid}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-slate-900">{label}</div>
        <div className="text-[11.5px] text-slate-500 mt-0.5 leading-snug">{description}</div>
      </div>
      <span
        className={`relative inline-flex items-center h-5 w-9 rounded-full transition-colors flex-shrink-0 mt-0.5 ${
          checked ? "bg-[#0055FF]" : "bg-slate-200"
        } ${disabled ? "" : "hover:opacity-90"}`}
      >
        <span
          className={`absolute size-4 rounded-full bg-white shadow-sm transition-transform ${
            checked ? "translate-x-[18px]" : "translate-x-0.5"
          }`}
        />
        <input
          type="checkbox"
          checked={!!checked}
          disabled={disabled}
          onChange={onChange}
          className="sr-only"
        />
      </span>
    </label>
  );
}
